/**
 * Texti tab: large, independently scrolling lyrics with remembered A−/A+ size and a high-contrast
 * mode. Line breaks are preserved exactly (white-space: pre-wrap); text is rendered as plain text.
 */
import { AArrowDown, AArrowUp, Contrast, FileText } from 'lucide-react';
import { useEffect, useState, useSyncExternalStore } from 'react';
import type { SongFileDTO } from '../../shared/dto/library';
import { cx, EmptyState } from '../components/ui';
import { useT } from '../i18n';
import { FileChips } from './FileChips';
import { PREF, readPref, writePref } from './storage';

export const LYRICS_MIN = 16;
export const LYRICS_MAX = 44;
export const LYRICS_DEFAULT = 22;
const LYRICS_STEP = 2;

const isSize = (v: unknown): v is number => typeof v === 'number' && v >= LYRICS_MIN && v <= LYRICS_MAX;
const isBool = (v: unknown): v is boolean => typeof v === 'boolean';

function subscribeScheme(cb: () => void) {
  const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
  mq?.addEventListener('change', cb);
  const mo = new MutationObserver(cb);
  mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
  return () => {
    mq?.removeEventListener('change', cb);
    mo.disconnect();
  };
}

/** Same rule as styles.css: data-theme wins, otherwise the system preference. */
function isDark(): boolean {
  const theme = document.documentElement.dataset.theme;
  if (theme === 'dark') return true;
  if (theme === 'light') return false;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches === true;
}

export function LyricsView({
  files,
  selectedId,
  onSelect,
}: {
  files: SongFileDTO[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const t = useT();
  const [size, setSize] = useState(() => readPref(PREF.lyricsSize, LYRICS_DEFAULT, isSize));
  const [contrast, setContrast] = useState(() => readPref(PREF.lyricsContrast, false, isBool));
  const dark = useSyncExternalStore(subscribeScheme, isDark, () => false);
  const file = files.find((f) => f.id === selectedId) ?? files[0] ?? null;

  useEffect(() => writePref(PREF.lyricsSize, size === LYRICS_DEFAULT ? null : size), [size]);
  useEffect(() => writePref(PREF.lyricsContrast, contrast || null), [contrast]);

  const text = file?.lyricsText?.trim() ? file.lyricsText : null;
  const hc = contrast ? (dark ? 'bg-black text-white' : 'bg-white text-black') : 'bg-[var(--surface)] text-[var(--ink)]';

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-[var(--line)] bg-[var(--surface)] px-3 py-2">
        <div className="min-w-0 flex-1">
          {files.length > 1 ? <FileChips files={files} selectedId={file?.id ?? null} onSelect={onSelect} label={t('viewer.lyrics_files_label')} /> : null}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setSize((s) => Math.max(LYRICS_MIN, s - LYRICS_STEP))}
            disabled={size <= LYRICS_MIN}
            aria-label={t('viewer.lyrics_smaller')}
            title={t('viewer.lyrics_smaller')}
            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl border border-[var(--line)] bg-[var(--surface)] disabled:opacity-40"
          >
            <AArrowDown className="size-6" aria-hidden />
          </button>
          <span className="w-10 text-center text-[15px] tabular-nums text-[var(--muted)]" aria-label={t('viewer.lyrics_size', { size })}>
            {size}
          </span>
          <button
            type="button"
            onClick={() => setSize((s) => Math.min(LYRICS_MAX, s + LYRICS_STEP))}
            disabled={size >= LYRICS_MAX}
            aria-label={t('viewer.lyrics_larger')}
            title={t('viewer.lyrics_larger')}
            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl border border-[var(--line)] bg-[var(--surface)] disabled:opacity-40"
          >
            <AArrowUp className="size-6" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => setContrast((c) => !c)}
            aria-pressed={contrast}
            title={t('viewer.lyrics_contrast')}
            className={cx(
              'ml-1 inline-flex min-h-11 items-center gap-2 rounded-xl border px-3 font-semibold',
              contrast ? 'border-transparent bg-[var(--accent)] text-[var(--accent-ink)]' : 'border-[var(--line)] bg-[var(--surface)] text-[var(--ink-2)]',
            )}
          >
            <Contrast className="size-5" aria-hidden />
            <span className="hidden sm:inline">{t('viewer.lyrics_contrast')}</span>
          </button>
        </div>
      </div>
      <div className={cx('scroll-y min-h-0 flex-1', hc)}>
        {text ? (
          <div
            className={cx('mx-auto max-w-3xl px-5 py-6 break-words whitespace-pre-wrap sm:px-8', contrast && 'font-semibold')}
            style={{ fontSize: size, lineHeight: 1.55 }}
          >
            {text}
          </div>
        ) : (
          <EmptyState icon={FileText} title={t('viewer.lyrics_empty')}>
            {t('viewer.lyrics_empty_hint')}
          </EmptyState>
        )}
      </div>
    </div>
  );
}
